import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Calculator, TrendingDown, CheckCircle2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { ThemeToggle } from "@/components/ThemeToggle";
import { requestAccessCsv } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

const CostCalculator = () => {
  const [engineers, setEngineers] = useState<number>(10);
  const [avgSalary, setAvgSalary] = useState<number>(150000);
  const [issuesPerEngineer, setIssuesPerEngineer] = useState<number>(2.5);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();
  
  const issuesPerWeek = engineers * issuesPerEngineer;

  // Calculations
  const hoursPerIssue = 4; // Average hours to fix a bug manually
  const healionMinutesPerIssue = 15; // AI fixes in 15 minutes
  const workWeeksPerYear = 50;
  const hoursPerWeek = 40;
  
  const hourlyRate = avgSalary / (workWeeksPerYear * hoursPerWeek);
  
  // Current cost calculations
  const totalHoursPerWeek = issuesPerWeek * hoursPerIssue;
  const engineersNeededForBugs = totalHoursPerWeek / hoursPerWeek;
  const currentCostPerWeek = totalHoursPerWeek * hourlyRate;
  const currentCostPerYear = currentCostPerWeek * workWeeksPerYear;
  
  // With Healion calculations
  const healionHoursPerWeek = issuesPerWeek * (healionMinutesPerIssue / 60);
  const healionCostPerIssue = 12; // Cost per AI fix
  const healionWeeklyCost = issuesPerWeek * healionCostPerIssue;
  const healionYearlyCost = healionWeeklyCost * workWeeksPerYear;
  const engineerTimeFreed = totalHoursPerWeek - healionHoursPerWeek; // Review time
  const engineerCostSaved = engineerTimeFreed * hourlyRate * workWeeksPerYear;
  
  const totalSavings = currentCostPerYear - (healionYearlyCost + (healionHoursPerWeek * hourlyRate * workWeeksPerYear));
  const savingsPercentage = ((totalSavings / currentCostPerYear) * 100).toFixed(1);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) {
      return;
    }

    if (!email || !email.includes("@")) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter a valid email address",
        duration: 5000,
      });
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response: any = await requestAccessCsv(email);
      if (response && response?.status && response?.status >= 400) {
        throw new Error(response.statusText);
      }
      setIsSubmitted(true);
      toast({
        title: "🎉 You're on the list!",
        description: "Welcome aboard, future bug-free hero! We'll notify you when AI starts crushing your production bugs.",
        duration: 5000,
      });
    } catch (err) {
      console.error(err);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please refresh the page and try again.",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-background/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <ThemeToggle />
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 sm:py-8 max-w-7xl">
        {/* Title */}
        <div className="text-center mb-4">
          <div className="inline-flex items-center gap-2 px-2 py-1 rounded-full bg-muted border border-border mb-2">
            <Calculator className="w-3 h-3 text-foreground/60" />
            <span className="text-xs font-medium text-foreground/80">ROI Calculator</span>
          </div>
          <h1 className="font-heading text-xl sm:text-2xl font-bold mb-1 tracking-tight">
            Calculate Your Savings
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground max-w-2xl mx-auto">
            Time and money saved with Healion
          </p>
        </div>

        <div className="grid md:grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Input Section */}
          <Card className="p-3 sm:p-4 border-border">
            <h2 className="font-heading text-base font-bold mb-3">Your Current Setup</h2>
            
            <div className="space-y-3">
              <div>
                <Label htmlFor="engineers" className="text-xs font-medium mb-1 block">
                  Number of Engineers
                </Label>
                <Input
                  id="engineers"
                  type="number"
                  min="1"
                  value={engineers}
                  onChange={(e) => setEngineers(Number(e.target.value) || 1)}
                  className="text-sm h-8"
                />
                <p className="text-xs text-muted-foreground mt-0.5">Total engineers on your team</p>
              </div>

              <div>
                <Label htmlFor="issuesPerEngineer" className="text-xs font-medium mb-1 block">
                  Issues Per Engineer Per Week
                </Label>
                <Input
                  id="issuesPerEngineer"
                  type="number"
                  min="0"
                  step="0.1"
                  value={issuesPerEngineer}
                  onChange={(e) => setIssuesPerEngineer(Number(e.target.value) || 0)}
                  className="text-sm h-8"
                />
                <p className="text-xs text-muted-foreground mt-0.5">Total: {issuesPerWeek} issues/week across team</p>
              </div>

              <div>
                <Label htmlFor="salary" className="text-xs font-medium mb-1 block">
                  Average Engineer Salary (Annual)
                </Label>
                <Input
                  id="salary"
                  type="number"
                  min="0"
                  step="1000"
                  value={avgSalary}
                  onChange={(e) => setAvgSalary(Number(e.target.value))}
                  className="text-sm h-8"
                />
                <p className="text-xs text-muted-foreground mt-0.5">{formatCurrency(hourlyRate)}/hour</p>
              </div>


              <div className="pt-2 border-t border-border">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Time spent on bugs weekly</span>
                  <span className="font-semibold">{totalHoursPerWeek.toFixed(1)} hours</span>
                </div>
                <div className="flex items-center justify-between text-xs mt-1">
                  <span className="text-muted-foreground">Engineer capacity used</span>
                  <span className="font-semibold">{engineersNeededForBugs.toFixed(1)} FTE</span>
                </div>
              </div>
            </div>
          </Card>

          {/* Results Section */}
          <div className="space-y-3">
            {/* Today's Cost */}
            <Card className="p-3 sm:p-4 border-destructive/20 bg-destructive/5">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-heading text-sm font-semibold text-muted-foreground mb-0.5">
                    Today : You Fix Production Bugs
                  </h3>
                  <p className="text-xs text-muted-foreground">Current annual cost</p>
                </div>
                <div className="text-lg">🔴</div>
              </div>
              <div className="text-2xl sm:text-3xl font-bold mb-2">
                {formatCurrency(currentCostPerYear)}
              </div>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Weekly cost:</span>
                  <span className="font-medium">{formatCurrency(currentCostPerWeek)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Hours per issue:</span>
                  <span className="font-medium">{hoursPerIssue}h</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Engineer burnout:</span>
                  <span className="font-medium">High 😴</span>
                </div>
              </div>
            </Card>

            {/* Tomorrow's Cost */}
            <Card className="p-3 sm:p-4 border-primary/20 bg-primary/5">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-heading text-sm font-semibold text-muted-foreground mb-0.5">
                    Tomorrow: You Only Review AI Fix
                  </h3>
                  <p className="text-xs text-muted-foreground">Projected annual cost</p>
                </div>
                <div className="text-lg">✅</div>
              </div>
              <div className="text-2xl sm:text-3xl font-bold mb-2">
                {formatCurrency(healionYearlyCost + (healionHoursPerWeek * hourlyRate * workWeeksPerYear))}
              </div>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Healion yearly:</span>
                  <span className="font-medium">{formatCurrency(healionYearlyCost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Review time yearly:</span>
                  <span className="font-medium">{formatCurrency(healionHoursPerWeek * hourlyRate * workWeeksPerYear)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Minutes per issue:</span>
                  <span className="font-medium">{healionMinutesPerIssue}min</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Engineer well-being:</span>
                  <span className="font-medium">Excellent 😊</span>
                </div>
              </div>
            </Card>

            {/* Savings Summary */}
            <Card className="p-3 sm:p-4 border-2 border-accent bg-gradient-to-br from-accent/20 via-accent/15 to-accent/5 shadow-lg relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-accent/10 to-transparent animate-pulse-slow"></div>
              <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingDown className="w-4 h-4 text-black dark:text-white animate-bounce" />
                  <h3 className="font-heading text-base font-bold text-black dark:text-white">Annual Savings 💰</h3>
                </div>
                <div className="text-3xl sm:text-4xl font-bold text-black dark:text-white mb-1 drop-shadow-[0_0_8px_rgba(251,146,60,0.3)] dark:drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]">
                  {formatCurrency(totalSavings)}
                </div>
                <p className="text-xs text-black/80 dark:text-white/90 font-medium mb-2">
                  That's a {savingsPercentage}% reduction in debugging costs
                </p>
                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-black/30 dark:border-white/30">
                  <div>
                    <p className="text-xs text-black/60 dark:text-white/70 mb-0.5">Time Freed</p>
                    <p className="text-base font-bold text-black dark:text-white">{(engineerTimeFreed * workWeeksPerYear).toFixed(0)}h/year</p>
                  </div>
                  <div>
                    <p className="text-xs text-black/60 dark:text-white/70 mb-0.5">Capacity Gain</p>
                    <p className="text-base font-bold text-black dark:text-white">{(engineerTimeFreed / hoursPerWeek).toFixed(1)} FTE</p>
                  </div>
                </div>
              </div>
            </Card>

            {!isSubmitted ? (
              <form onSubmit={handleSubmit} className="space-y-3">
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-10 text-sm bg-background border-border focus:border-foreground"
                  disabled={isSubmitting}
                />
                <Button 
                  type="submit" 
                  variant="hero" 
                  size="lg"
                  className="w-full h-10 text-sm hover:scale-105 transition-transform"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Joining..." : "Join Waitlist - Get 100 Free Fixes"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>
            ) : (
              <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-accent/10 border border-accent/30">
                <CheckCircle2 className="w-6 h-6 text-accent flex-shrink-0" />
                <div className="text-left">
                  <p className="font-medium text-foreground text-sm">You're in! 🚀</p>
                  <p className="text-xs text-muted-foreground">We'll reach out when it's your turn to experience AI-powered debugging magic.</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Note */}
        <div className="mt-4 p-3 rounded-lg bg-muted/30 border border-border">
          <p className="text-xs text-muted-foreground text-center">
            <strong className="text-foreground">Your team reviews code. AI writes it.</strong> These calculations assume 4 hours average per manual bug fix 
            vs. 15 minutes with AI automation. Actual results may vary based on complexity and team size.
          </p>
        </div>
      </main>
    </div>
  );
};

export default CostCalculator;
